/**
 * formsInbox.service.ts
 *
 * API client for the Forms Inbox (legacy parity with
 * `message_center-test.php?v=survey`). The list, detail viewer, and
 * action endpoints back FormsInboxPage / FormInboxResponseModal.
 */
import { apiClient } from './apiClient';
import type { ApiResponse, PaginatedResponse } from '@/types/api.types';
import type { SurveyResponseDetail } from './patientForms.service';
import { COMPLETED_FORM_PDF_REQUEST, toCompletedFormPdf, type CompletedFormPdf } from './completedFormPdf';

// ─── Types ────────────────────────────────────────────────────────────────────

export type FormsInboxStatus = 'unread' | 'read' | 'incomplete';

export type FormsInboxSortBy = 'date' | 'name' | 'subject' | 'doctor' | 'coordinator';

/** The lead a form with no patient came in as (SCRUM-2419). */
export interface FormsInboxLead {
  id: number;
  name: string;
}

/** The patient Forms tab's payload, plus the lead for a form with no patient. */
export interface FormsInboxResponseDetail extends SurveyResponseDetail {
  lead?: FormsInboxLead | null;
}

export interface FormsInboxRow {
  id: number;
  date: string | null;
  dateCompleted: string | null;
  wasRead: boolean;
  patientId: number;
  planId: number;
  surveyId: number;
  subject: string;
  fromName: string;
  doctor: string;
  coordinator: string;
  doctorId: number;
  coordinatorId: number;
}

/** The sidebar filters. The list and the Unread count both take them. */
export interface FormsInboxFilterParams {
  search?: string;
  /** Optional client-side overrides; usually we rely on the server-saved
   *  selections from `/forms/inbox/filters/saved`. Provided here for
   *  completeness when callers want to bypass persistence. */
  dateFrom?: string;
  dateTo?: string;
  surveys?: number[];
  surveysExclude?: boolean;
  doctors?: number[];
  doctorsExclude?: boolean;
  coordinators?: number[];
  coordinatorsExclude?: boolean;
}

export interface FormsInboxListParams extends FormsInboxFilterParams {
  status?: FormsInboxStatus;
  page?: number;
  perPage?: number;
  sortBy?: FormsInboxSortBy;
  sortDir?: 'asc' | 'desc';
}

export interface FormsInboxFilterOption {
  value: number;
  label: string;
}

export interface FormsInboxFilterOptions {
  eforms: FormsInboxFilterOption[];
  doctors: FormsInboxFilterOption[];
  coordinators: FormsInboxFilterOption[];
  /** The account has e-form partial saves on — only then is there an Incomplete tab. */
  partialSaves?: boolean;
}

export interface SavedCheckList {
  values: number[];
  plus: boolean;
}

export interface SavedDateRange {
  from: string;
  to: string;
  preset: string;
}

export interface FormsInboxSavedFilters {
  eform: SavedCheckList;
  doctor: SavedCheckList;
  coordinator: SavedCheckList;
  daterange: SavedDateRange;
}

export type SavedCheckListType = 'eform' | 'doctor' | 'coordinator';

export interface SaveCheckListRequest {
  type: SavedCheckListType;
  values: number[];
  plus: boolean;
}

export interface SaveDateRangeRequest {
  type: 'daterange';
  dateFrom: string;
  dateTo: string;
  preset?: string;
}

export type SaveFilterRequest = SaveCheckListRequest | SaveDateRangeRequest;

// ─── Endpoints ────────────────────────────────────────────────────────────────

function filterQuery(params: FormsInboxFilterParams): Record<string, unknown> {
  const query: Record<string, unknown> = {};
  if (params.search) query.search = params.search;
  if (params.dateFrom) query.dateFrom = params.dateFrom;
  if (params.dateTo) query.dateTo = params.dateTo;
  if (params.surveys && params.surveys.length) query.surveys = params.surveys;
  if (params.surveysExclude) query.surveysExclude = '1';
  if (params.doctors && params.doctors.length) query.doctors = params.doctors;
  if (params.doctorsExclude) query.doctorsExclude = '1';
  if (params.coordinators && params.coordinators.length) query.coordinators = params.coordinators;
  if (params.coordinatorsExclude) query.coordinatorsExclude = '1';
  return query;
}

export function listFormsInbox(params: FormsInboxListParams = {}): Promise<PaginatedResponse<FormsInboxRow>> {
  const query = filterQuery(params);
  if (params.status) query.status = params.status;
  if (params.page) query.page = params.page;
  if (params.perPage) query.perPage = params.perPage;
  if (params.sortBy) query.sortBy = params.sortBy;
  if (params.sortDir) query.sortDir = params.sortDir;

  return apiClient
    .get<PaginatedResponse<FormsInboxRow>>('/forms/inbox', { params: query })
    .then((r) => r.data);
}

export function getFormsInboxFilterOptions(): Promise<FormsInboxFilterOptions> {
  return apiClient
    .get<ApiResponse<FormsInboxFilterOptions>>('/forms/inbox/filters')
    .then((r) => r.data.data);
}

export function getSavedFormsInboxFilters(): Promise<FormsInboxSavedFilters> {
  return apiClient
    .get<ApiResponse<FormsInboxSavedFilters>>('/forms/inbox/filters/saved')
    .then((r) => r.data.data);
}

export function saveFormsInboxFilter(req: SaveFilterRequest): Promise<FormsInboxSavedFilters> {
  return apiClient
    .put<ApiResponse<FormsInboxSavedFilters>>('/forms/inbox/filters/saved', req)
    .then((r) => r.data.data);
}

/** How many rows the Unread tab lists under these filters. */
export function getFormsInboxUnreadCount(filters: FormsInboxFilterParams = {}): Promise<number> {
  return apiClient
    .get<ApiResponse<{ count: number }>>('/forms/inbox/unread-count', { params: filterQuery(filters) })
    .then((r) => r.data.data?.count ?? 0);
}

export function getFormsInboxResponseDetail(responseId: number): Promise<FormsInboxResponseDetail> {
  return apiClient
    .get<ApiResponse<FormsInboxResponseDetail>>(`/forms/inbox/responses/${responseId}`)
    .then((r) => r.data.data);
}

/** The completed form's PDF by response id alone, so lead and unattached forms (patientId 0) work. */
export function getFormsInboxResponsePdf(responseId: number): Promise<CompletedFormPdf> {
  return apiClient
    .get<Blob>(`/forms/inbox/responses/${responseId}/pdf`, COMPLETED_FORM_PDF_REQUEST)
    .then(toCompletedFormPdf);
}

export function markFormsInboxResponseRead(responseId: number): Promise<void> {
  return apiClient
    .post(`/forms/inbox/responses/${responseId}/mark-read`)
    .then(() => undefined);
}

export function markFormsInboxResponseUnread(responseId: number): Promise<void> {
  return apiClient
    .post(`/forms/inbox/responses/${responseId}/mark-unread`)
    .then(() => undefined);
}

export function bulkMarkFormsInboxRead(ids: number[]): Promise<number> {
  return apiClient
    .post<ApiResponse<{ updated: number }>>('/forms/inbox/responses/bulk-read', { ids })
    .then((r) => r.data.data?.updated ?? 0);
}

export function completeFormsInboxResponse(responseId: number): Promise<void> {
  return apiClient
    .post(`/forms/inbox/responses/${responseId}/complete`)
    .then(() => undefined);
}

export function trashFormsInboxResponse(responseId: number): Promise<void> {
  return apiClient
    .post(`/forms/inbox/responses/${responseId}/trash`)
    .then(() => undefined);
}

export function restoreFormsInboxResponse(responseId: number): Promise<void> {
  return apiClient
    .post(`/forms/inbox/responses/${responseId}/restore`)
    .then(() => undefined);
}

export function sendFormsInboxResponseToNotes(
  responseId: number,
): Promise<{ success: boolean; noteId: number; message: string }> {
  return apiClient
    .post<ApiResponse<{ success: boolean; noteId: number; message: string }>>(
      `/forms/inbox/responses/${responseId}/send-to-notes`,
    )
    .then((r) => r.data.data);
}
